import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, ArrowRight } from "lucide-react";
import client, { formatApiError } from "@/lib/api";
import AuthShell, { AuthField, authInputClass } from "@/components/AuthShell";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) { setErr("The two passwords don't match."); return; }
    setLoading(true);
    try {
      await client.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell
      testId="reset-password-page"
      title="Set a new password"
      subtitle="Choose something at least 8 characters long."
      tagline="Almost there."
      taglineBody="Once it's saved, sign in with your new password."
      footer={<Link to="/login" className="font-bold text-hv-teal hover:underline">Back to sign in</Link>}
    >
      {done ? (
        <div className="space-y-4">
          <div className="text-hv-navy" data-testid="reset-password-done">Your password has been updated.</div>
          <button onClick={() => navigate("/login")} className="hv-btn-primary w-full">Sign in</button>
        </div>
      ) : !token ? (
        <div className="text-sm text-hv-coral font-bold" data-testid="reset-password-missing-token">
          This page needs a reset link from your email.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <AuthField icon={<Lock size={18} strokeWidth={2.4} />} label="New password">
            <input data-testid="reset-password-new" type="password" required minLength={8} autoFocus
                   value={password} onChange={(e) => setPassword(e.target.value)} className={authInputClass} />
          </AuthField>
          <AuthField icon={<Lock size={18} strokeWidth={2.4} />} label="Confirm password">
            <input data-testid="reset-password-confirm" type="password" required minLength={8}
                   value={confirm} onChange={(e) => setConfirm(e.target.value)} className={authInputClass} />
          </AuthField>
          {err && <div className="text-sm text-hv-coral font-bold" data-testid="reset-password-error">{err}</div>}
          <button type="submit" disabled={loading} data-testid="reset-password-submit"
                  className="hv-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-70">
            {loading ? "…" : <>Save password <ArrowRight size={18} strokeWidth={2.6} /></>}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
